import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAllEpisodeNotes, type EpisodeNote } from "@/hooks/useEpisodeNotes";
import { StickyNote, Search, Trash2, Play, Pencil, Check, X, Share2 } from "lucide-react";
import AudioPlayer from "@/components/AudioPlayer";
import EmptyNotes from "@/components/empty-states/EmptyNotes";
import { toast } from "sonner";

function formatTimestamp(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

const OG_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/episode-og`;

function buildNoteShareText(note: EpisodeNote, mode: "episode" | "with-notes", allNotes: EpisodeNote[]): { text: string; url: string } {
  const ep = note.episode;
  const podcast = ep?.podcast;
  const slug = ep?.slug || "";
  const url = `${OG_BASE}?slug=${encodeURIComponent(slug)}`;

  if (mode === "episode") {
    return {
      text: `${ep?.title || "Episode"}${podcast?.name ? ` — ${podcast.name}` : ""}`,
      url,
    };
  }

  const episodeNotes = allNotes.filter((n) => n.episode_id === note.episode_id);
  const lines = [
    `📝 My notes on "${ep?.title || "Episode"}"${podcast?.name ? ` (${podcast.name})` : ""}`,
    "",
    ...episodeNotes.map((n) => {
      const ts = n.timestamp_seconds !== null ? `[${formatTimestamp(n.timestamp_seconds)}] ` : "";
      return `• ${ts}${n.content}`;
    }),
    "",
    url,
  ];
  return { text: lines.join("\n"), url };
}

/* ── Individual speech bubble for a note ── */
function NoteBubble({
  note,
  index,
  allNotes,
  playingNoteId,
  setPlayingNoteId,
  editingNoteId,
  setEditingNoteId,
  editContent,
  setEditContent,
  editRef,
  shareMenuNoteId,
  setShareMenuNoteId,
  handleShare,
  updateNote,
  deleteNote,
}: {
  note: EpisodeNote;
  index: number;
  allNotes: EpisodeNote[];
  playingNoteId: string | null;
  setPlayingNoteId: (id: string | null) => void;
  editingNoteId: string | null;
  setEditingNoteId: (id: string | null) => void;
  editContent: string;
  setEditContent: (c: string) => void;
  editRef: React.RefObject<HTMLTextAreaElement>;
  shareMenuNoteId: string | null;
  setShareMenuNoteId: (id: string | null) => void;
  handleShare: (note: EpisodeNote, mode: "episode" | "with-notes") => void;
  updateNote: (id: string, content: string) => void;
  deleteNote: (id: string) => void;
}) {
  const ep = note.episode;
  const podcast = ep?.podcast;
  const tailLeft = index % 2 === 0;
  const bgColor = tailLeft ? "#FFFEF2" : "#F4F8FF";

  return (
    <div className="relative" style={{ paddingBottom: "18px" }}>
      {/* Bubble */}
      <div
        className="relative"
        style={{
          background: bgColor,
          border: "3px solid hsl(var(--foreground))",
          borderRadius: "3px",
          boxShadow: "4px 4px 0px hsl(var(--foreground))",
          overflow: "visible",
        }}
      >
        {/* Halftone dot pattern */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage: "radial-gradient(circle, hsl(var(--foreground)) 1px, transparent 1px)",
            backgroundSize: "8px 8px",
          }}
        />

        <div className="relative px-4 sm:px-5 py-3 sm:py-4">
          {/* Top row: artwork + episode info + actions */}
          <div className="flex items-start gap-3">
            {/* Podcast artwork */}
            {podcast?.artwork_url ? (
              <img
                src={podcast.artwork_url}
                alt=""
                className="w-10 h-10 sm:w-11 sm:h-11 retro-border object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 sm:w-11 sm:h-11 retro-border bg-muted flex items-center justify-center flex-shrink-0">
                <StickyNote className="w-4 h-4 text-muted-foreground" />
              </div>
            )}

            {/* Episode / podcast info */}
            <div className="flex-1 min-w-0">
              {ep?.slug ? (
                <Link
                  to={`/episode/${ep.slug}`}
                  className="block font-mono text-[10px] sm:text-[11px] text-muted-foreground hover:text-primary transition-colors hover:underline truncate"
                >
                  {ep.title}
                </Link>
              ) : (
                <span className="block font-mono text-[10px] sm:text-[11px] text-muted-foreground truncate">
                  {ep?.title || "Unknown episode"}
                </span>
              )}
              <span className="block font-mono text-[10px] sm:text-[11px] text-muted-foreground/70 truncate">
                {podcast?.name || "Unknown podcast"}
              </span>
            </div>

            {/* Actions — inline with header row */}
            <div className="flex items-center gap-0.5 flex-shrink-0 relative">
              <button
                onClick={() => {
                  setEditingNoteId(note.id);
                  setEditContent(note.content);
                }}
                className="p-1 text-muted-foreground hover:text-primary transition-colors"
                title="Edit note"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <div className="relative">
                <button
                  onClick={() => setShareMenuNoteId(shareMenuNoteId === note.id ? null : note.id)}
                  className="p-1 text-muted-foreground hover:text-primary transition-colors"
                  title="Share"
                >
                  <Share2 className="w-3.5 h-3.5" />
                </button>
                {shareMenuNoteId === note.id && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShareMenuNoteId(null)} />
                    <div className="absolute right-0 top-full mt-1 z-20 retro-border retro-shadow bg-card py-1 w-48">
                      <button
                        onClick={() => handleShare(note, "episode")}
                        className="w-full text-left font-mono text-[11px] px-3 py-2 hover:bg-muted transition-colors"
                      >
                        Share episode link
                      </button>
                      <button
                        onClick={() => handleShare(note, "with-notes")}
                        className="w-full text-left font-mono text-[11px] px-3 py-2 hover:bg-muted transition-colors"
                      >
                        Share with all notes
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => deleteNote(note.id)}
                className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                title="Delete note"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Note content */}
          <div className="mt-2">
            {editingNoteId === note.id ? (
              <div className="space-y-2">
                <textarea
                  ref={editRef}
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={3}
                  className="w-full text-base sm:text-lg leading-snug px-2 py-1.5 bg-background border border-primary focus:outline-none resize-none rounded"
                  style={{ fontFamily: "'Architects Daughter', cursive" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if (editContent.trim()) {
                        updateNote(note.id, editContent.trim());
                        setEditingNoteId(null);
                      }
                    }
                    if (e.key === "Escape") setEditingNoteId(null);
                  }}
                />
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      if (editContent.trim()) {
                        updateNote(note.id, editContent.trim());
                        setEditingNoteId(null);
                      }
                    }}
                    className="font-mono text-[10px] font-bold px-2.5 py-1 bg-primary text-primary-foreground hover:bg-retro-amber transition-colors flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" /> Save
                  </button>
                  <button
                    onClick={() => setEditingNoteId(null)}
                    className="font-mono text-[10px] px-2.5 py-1 text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p
                className="text-base sm:text-lg leading-snug text-foreground"
                style={{ fontFamily: "'Architects Daughter', cursive" }}
              >
                {note.content}
              </p>
            )}
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5">
            {note.timestamp_seconds !== null && (
              <button
                onClick={() => setPlayingNoteId(playingNoteId === note.id ? null : note.id)}
                className="inline-flex items-center gap-1 font-mono text-[10px] text-primary font-bold hover:underline transition-colors"
                title="Play from this timestamp"
              >
                ▶ {formatTimestamp(note.timestamp_seconds)}
              </button>
            )}
            <span className="font-mono text-[10px] text-muted-foreground">
              {new Date(note.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>

          {/* Inline audio player */}
          {playingNoteId === note.id && ep?.audio_url && (
            <div className="mt-2 border-t border-dashed border-border pt-2">
              <AudioPlayer
                src={ep.audio_url}
                title={ep.title}
                podcastName={podcast?.name}
                artworkUrl={podcast?.artwork_url || undefined}
                episodeId={note.episode_id}
                startTime={note.timestamp_seconds ?? undefined}
                autoPlay
              />
            </div>
          )}
        </div>

        {/* Speech bubble tail — seamless connection */}
        {tailLeft ? (
          <svg
            className="absolute left-5 pointer-events-none"
            style={{ bottom: "-15px" }}
            width="32"
            height="18"
            viewBox="0 0 32 18"
          >
            {/* Outer border shape — extends 4px into bubble to fully cover border */}
            <polygon points="-2,-4 33,-4 7,18" fill="hsl(var(--foreground))" />
            {/* Inner fill — matches bubble bg, inset by border width */}
            <polygon points="1,-4 30,-4 8,14" fill={bgColor} />
          </svg>
        ) : (
          <svg
            className="absolute right-5 pointer-events-none"
            style={{ bottom: "-15px" }}
            width="32"
            height="18"
            viewBox="0 0 32 18"
          >
            <polygon points="-1,-4 34,-4 25,18" fill="hsl(var(--foreground))" />
            <polygon points="2,-4 31,-4 24,14" fill={bgColor} />
          </svg>
        )}
      </div>
    </div>
  );
}

/* ── Main NotesLibrary ── */
export default function NotesLibrary() {
  const { notes, loading, updateNote, deleteNote } = useAllEpisodeNotes();
  const [search, setSearch] = useState("");
  const [podcastFilter, setPodcastFilter] = useState<string>("all");
  const [playingNoteId, setPlayingNoteId] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [shareMenuNoteId, setShareMenuNoteId] = useState<string | null>(null);
  const editRef = useRef<HTMLTextAreaElement>(null);

  const handleShare = useCallback(async (note: EpisodeNote, mode: "episode" | "with-notes") => {
    const { text, url } = buildNoteShareText(note, mode, notes);
    setShareMenuNoteId(null);

    if (navigator.share) {
      try {
        await navigator.share({ title: note.episode?.title || "Episode", text, url });
        return;
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
      }
    }
    const fullText = mode === "episode" ? url : text;
    try {
      await navigator.clipboard.writeText(fullText);
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Failed to copy");
    }
  }, [notes]);

  useEffect(() => {
    if (editingNoteId && editRef.current) {
      editRef.current.focus();
      editRef.current.setSelectionRange(editRef.current.value.length, editRef.current.value.length);
    }
  }, [editingNoteId]);

  const podcasts = useMemo(() => {
    const map = new Map<string, { name: string; artwork_url: string | null }>();
    notes.forEach((n) => {
      const podcast = n.episode?.podcast;
      if (podcast && !map.has(podcast.name)) {
        map.set(podcast.name, podcast);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [notes]);

  const filtered = useMemo(() => {
    return notes.filter((n) => {
      if (podcastFilter !== "all" && n.episode?.podcast?.name !== podcastFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchContent = n.content.toLowerCase().includes(q);
        const matchEpisode = n.episode?.title?.toLowerCase().includes(q);
        const matchPodcast = n.episode?.podcast?.name?.toLowerCase().includes(q);
        if (!matchContent && !matchEpisode && !matchPodcast) return false;
      }
      return true;
    });
  }, [notes, search, podcastFilter]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="retro-border bg-card p-4 animate-pulse h-24" />
        ))}
      </div>
    );
  }

  if (notes.length === 0) {
    return <EmptyNotes />;
  }

  return (
    <div className="space-y-4">
      {/* Search + filter bar */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notes…"
            className="w-full font-mono text-xs pl-9 pr-3 py-2 bg-background border border-border focus:outline-none focus:border-primary"
          />
        </div>
        {podcasts.length > 1 && (
          <select
            value={podcastFilter}
            onChange={(e) => setPodcastFilter(e.target.value)}
            className="font-mono text-xs px-3 py-2 bg-background border border-border focus:outline-none focus:border-primary"
          >
            <option value="all">All Podcasts</option>
            {podcasts.map(([name]) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="text-lg">💬</span>
        <h2
          className="text-xl sm:text-2xl text-foreground tracking-wide"
          style={{ fontFamily: "'Bangers', cursive", letterSpacing: "0.05em" }}
        >
          Your Notes
        </h2>
        <span className="font-mono text-[10px] text-muted-foreground ml-auto">
          {filtered.length} note{filtered.length !== 1 ? "s" : ""}
          {search || podcastFilter !== "all" ? " (filtered)" : ""}
        </span>
      </div>

      {/* Individual speech bubbles */}
      <div className="space-y-5">
        {filtered.map((note, index) => (
          <NoteBubble
            key={note.id}
            note={note}
            index={index}
            allNotes={notes}
            playingNoteId={playingNoteId}
            setPlayingNoteId={setPlayingNoteId}
            editingNoteId={editingNoteId}
            setEditingNoteId={setEditingNoteId}
            editContent={editContent}
            setEditContent={setEditContent}
            editRef={editRef}
            shareMenuNoteId={shareMenuNoteId}
            setShareMenuNoteId={setShareMenuNoteId}
            handleShare={handleShare}
            updateNote={updateNote}
            deleteNote={deleteNote}
          />
        ))}
      </div>
    </div>
  );
}
